import { PriceDynamicsFilters } from '../../components/PriceDynamicsFilters/PriceDynamicsFilters';
import { EChartTypes, ERoomsCount, ITrendsDatasetItem, ValuationChartsWidget } from '@cian/valuation-web-charts-widget';
import { ChartInfo } from '../../components/ChartInfo';
import { PriceDynamicsInfo } from '../../components/PriceDynamicsInfo';
import { TrendsSpinner } from '../../components/TrendsSpinner';
import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { ILogger } from '@cian/logger';
import { IGetMarketTrandResponseSchema } from 'shared/repositories/valuation-offer-history/entities/market_trand/GetMarketTrandResponseSchema';
import { IGetAvailableFiltersForMarketTrandResponseSchema } from 'shared/repositories/valuation-offer-history/entities/market_trand/GetAvailableFiltersForMarketTrandResponseSchema';
import {
  EQueryParameters as EPeriod,
  EQueryParameters_1,
} from 'shared/repositories/valuation-offer-history/v1/get-market-trand';
import { today } from '../../utils/date';
import { useGetMinDate, useGetDiff, useGetPeriodText, useGetInfo, EGetInfo } from './hooks';
import { useDeviceType } from '@cian/ui-kit';
import { PriceDynamicWrapper } from '../../components/PriceDynamicWrapper';

interface IPriceDynamicsContainerProps
  extends Omit<Required<IGetMarketTrandResponseSchema>, 'marketTrand' | 'houseTrand'> {
  marketTrand: ITrendsDatasetItem[];
  houseTrand: ITrendsDatasetItem[];
  isLoading: boolean;
  filtersAvailability: IGetAvailableFiltersForMarketTrandResponseSchema;
  roomsCount: EQueryParameters_1;
  period: EPeriod;
  logger: ILogger;

  onRoomsCountChange: (roomsCount: EQueryParameters_1) => void;
  onPeriodChange: (period: EPeriod) => void;
}

export const PriceDynamicsContainer: FC<IPriceDynamicsContainerProps> = ({
  logger,
  roomsCount,
  period,
  diffPercent,
  diffPrice,
  diffPriceTrand,
  isLoading,
  averagePrice,
  averageSqmPrice,
  filtersAvailability,
  marketTrand: marketTrend,
  houseTrand: houseTrend,

  onRoomsCountChange,
  onPeriodChange,
}) => {
  const deviceType = useDeviceType();
  const dateMin = useGetMinDate(period);
  const differencePrice = useGetDiff({ diffPriceTrand, diffPrice, diffPercent });
  const isRoomsCountAll = roomsCount === EQueryParameters_1.All;
  const periodText = useGetPeriodText(period);
  const averageContent = useGetInfo({
    isLoading,
    averageSqmPrice,
    isRoomsCountAll,
    averagePrice,
    type: EGetInfo.PriceAverage,
  });
  const priceChangeContent = useGetInfo({
    isLoading,
    isRoomsCountAll,
    type: EGetInfo.PriceChange,
    differencePrice,
  });

  const chartInfoText = useMemo(
    () => ({
      magentaText: houseTrend.length > 0 ? 'Этот дом' : null,
      blueText: marketTrend.length > 0 ? 'Соседние дома' : null,
    }),
    [houseTrend, marketTrend],
  );

  const handleRoomsCountChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onRoomsCountChange(event.target.value as EQueryParameters_1);
    },
    [onRoomsCountChange],
  );

  const handlePeriodChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onPeriodChange(event.target.value as EPeriod);
    },
    [onPeriodChange],
  );

  const chartKey = `${diffPrice}${diffPercent}${diffPriceTrand}${marketTrend[0]?.value}${houseTrend[0]?.value}`;

  return (
    <PriceDynamicWrapper>
      <PriceDynamicsFilters
        filters={filtersAvailability}
        roomsCount={roomsCount}
        onRoomsCountChange={handleRoomsCountChange}
        period={period}
        onPeriodChange={handlePeriodChange}
        isDesktop={deviceType === 'desktop'}
      />
      {isLoading && <TrendsSpinner />}
      <PriceDynamicsInfo
        differencePrice={differencePrice}
        isRoomsCountAll={isRoomsCountAll}
        periodText={periodText}
        averageContent={averageContent}
        priceChangeContent={priceChangeContent}
      />
      <ChartInfo {...chartInfoText} />
      <ValuationChartsWidget
        key={chartKey}
        data={{
          dateMin,
          dateMax: today(),
          xScale: 'month',
          marketTrend,
          houseTrend,
        }}
        activeFilter={roomsCount as unknown as ERoomsCount}
        type={EChartTypes.Trends}
        logger={logger}
      />
    </PriceDynamicWrapper>
  );
};
